import React from 'react';
import { StageSection, Prose, FactGrid } from '../../ContentPrimitives';
import { StageBlock, content, question } from '../../types';

export const meta = {
    title: 'Accounts: Where All State Lives',
    tagline: 'Wallets, programs, and data are all the same kind of thing.',
};

// This stage expects 3 quiz questions configured in the admin panel, in this order:
//   Q0 - the owner field determines who can modify an account's data
//   Q1 - what a lamport is
//   Q2 - what kinds of things live inside accounts (wallets, programs, data)
export const blocks: StageBlock[] = [
    content('intro', () => (
        <StageSection gradientBorder eyebrow="Stage 2 of 8" title="Everything is an account">
            <Prose>
                Whether it's a wallet balance, a deployed program's code, a token mint, or an NFT's metadata, on Solana all of it
                lives inside the same kind of structure: an <strong>account</strong>. Every account has the same
                four core fields, regardless of what it's being used for.
            </Prose>
            <FactGrid items={[
                ['owner', 'The program allowed to modify this account\'s data. Only that program can write to it.'],
                ['lamports', 'The account\'s balance, denominated in lamports, the smallest unit of SOL.'],
                ['data', 'An arbitrary byte array. For a program account, this is executable code; for others, whatever that account\'s owner defines it to be.'],
                ['executable', 'A flag marking whether this account\'s data is a runnable program.'],
            ]} />
        </StageSection>
    )),

    question(0),

    content('lamports', () => (
        <StageSection title="Lamports: the smallest unit of SOL">
            <Prose>
                Just as Ethereum has wei and Bitcoin has satoshis, Solana denominates everything internally in
                lamports: one SOL equals one billion lamports. Every account's balance, every transaction fee, is
                ultimately just a lamport amount.
            </Prose>
        </StageSection>
    )),

    question(1),

    content('code-data-split', () => (
        <StageSection title="A deliberate split from Ethereum's model">
            <Prose>
                Ethereum bundles a smart contract's code and its storage together at a single address. Solana splits
                these apart on purpose: a program account holds only code (marked executable), while the actual
                mutable data that program reads and writes lives in completely separate accounts, passed into the
                program explicitly every time it's invoked. The next stage looks at exactly why that split matters.
            </Prose>
        </StageSection>
    )),

    question(2),
];
